import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  Copy, ExternalLink, Check, Search, Code2, LinkIcon, Globe, ShieldCheck,
  Sparkles, Calendar, BookOpen, Users, BriefcaseBusiness, Settings, Save,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = window.location.origin;

const SECTIONS = [
  {
    id: 'main',
    icon: Globe,
    title: 'Main Public Pages',
    description: 'Top-level navigation pages on dcaenterprisesinc.com',
    items: [
      { path: '/', name: 'Homepage', notes: 'Hero, services, testimonials carousel, portfolio' },
      { path: '/about', name: 'About', notes: 'Story, mission, photos' },
      { path: '/services', name: 'Services', notes: 'Services index' },
      { path: '/brands', name: 'Brands Portfolio', notes: 'All 11 ventures' },
      { path: '/resources', name: 'Resources Hub', notes: 'Business Jumpstart Framework + tools' },
      { path: '/events', name: 'Events', notes: 'Upcoming events' },
      { path: '/contact', name: 'Contact Us', notes: 'Contact form' },
      { path: '/programs', name: 'Programs', notes: 'All coaching programs comparison' },
    ],
  },
  {
    id: 'program-landings',
    icon: Sparkles,
    title: 'Program Landing Pages',
    description: 'Dedicated URLs per coaching program - share these directly in ads/social',
    items: [
      { path: '/free-jumpstart-session', name: 'FREE 20-Min Business Jumpstart Session', notes: 'Embedded scheduler · DCA-only' },
      { path: '/business-strategy-session', name: '1-on-1 Business Strategy Session ($197)', notes: 'Stripe checkout CTA' },
      { path: '/business-jumpstart-accelerator', name: 'Business Jumpstart Accelerator (3 mo)', notes: 'Application form CTA' },
      { path: '/business-growth-partnership', name: 'Business Growth Partnership (6 mo)', notes: 'Application form CTA' },
      { path: '/visionary-mentorship', name: 'Visionary CEO Mentorship (12 mo)', notes: 'Application form CTA · $4,997' },
      { path: '/program-application', name: 'Program Application Form', notes: 'Direct application form for 3/6/12-mo' },
    ],
  },
  {
    id: 'scheduling',
    icon: Calendar,
    title: 'Scheduling & Multi-Brand Coaching',
    description: 'Booking system URLs - central hub + per-brand pages',
    items: [
      { path: '/schedule', name: 'FREE Business Jumpstart Scheduler', notes: 'Standalone DCA scheduler' },
      { path: '/coaching', name: 'Coaching Hub (All Brands)', notes: 'Central booking hub for all 5 brands' },
      { path: '/manage-booking', name: 'Manage Booking', notes: 'Self-serve reschedule/cancel (token-protected)' },
    ],
  },
  {
    id: 'auth-portal',
    icon: ShieldCheck,
    title: 'Auth & Client Portal',
    description: 'Login, registration, and client-only pages',
    items: [
      { path: '/login', name: 'Login', notes: 'Combined client + admin login' },
      { path: '/register', name: 'Register', notes: 'New client signup' },
      { path: '/forgot-password', name: 'Forgot Password' },
      { path: '/portal', name: 'Client Dashboard', notes: 'Client-only' },
      { path: '/intake', name: 'Intake Form', notes: 'Post-booking intake' },
    ],
  },
];

const ADMIN_SECTION = {
  id: 'admin',
  icon: BriefcaseBusiness,
  title: 'Admin Dashboard',
  description: 'Admin-only URLs (require admin login)',
  items: [
    { path: '/admin', name: 'Admin Dashboard' },
    { path: '/admin/leads', name: 'Leads' },
    { path: '/admin/bookings', name: 'Bookings' },
    { path: '/admin/booking-brands', name: 'Booking Brands' },
    { path: '/admin/clients', name: 'Clients' },
    { path: '/admin/programs', name: 'Programs' },
    { path: '/admin/events', name: 'Events' },
    { path: '/admin/services', name: 'Services' },
    { path: '/admin/automation', name: 'Automation' },
    { path: '/admin/analytics', name: 'Analytics' },
    { path: '/admin/scheduling', name: 'Scheduling Rules' },
    { path: '/admin/contracts', name: 'Contracts' },
    { path: '/admin/links', name: 'Link Manager', notes: 'This page' },
    { path: '/admin/roadmap', name: 'Roadmap & Backlog' },
  ],
};

export default function AdminLinks() {
  const [brands, setBrands] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [conversions, setConversions] = useState({});
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(null);
  const [productionUrl, setProductionUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/booking-brands`).then((r) => setBrands(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/admin/link-analytics`, { withCredentials: true })
      .then((r) => {
        const map = {};
        (r.data || []).forEach((row) => { map[row.path] = row; });
        setAnalytics(map);
      })
      .catch(() => {});
    axios.get(`${API_URL}/api/admin/link-conversions`, { withCredentials: true })
      .then((r) => setConversions(r.data || {}))
      .catch(() => {});
    axios.get(`${API_URL}/api/site-settings`)
      .then((r) => setProductionUrl(r.data?.production_url || ''))
      .catch(() => {});
  }, []);

  // Effective origin: use production URL if set, otherwise window.location.origin
  const effectiveOrigin = (productionUrl && productionUrl.trim()) || ORIGIN;

  const saveProductionUrl = async () => {
    setSavingUrl(true);
    try {
      const cleaned = (productionUrl || '').trim().replace(/\/+$/, '');
      await axios.put(`${API_URL}/api/admin/site-settings`, { production_url: cleaned }, { withCredentials: true });
      setProductionUrl(cleaned);
      toast.success('Production URL saved - all links below now use it.');
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setSavingUrl(false);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const filter = (items) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((it) => (it.name + ' ' + (it.notes || '') + ' ' + it.path).toLowerCase().includes(q));
  };

  // Build dynamic brand sections
  const brandPagesItems = brands.map((b) => ({
    path: `/coaching/${b.slug}`,
    name: `${b.name} - On-DCA Booking Page`,
    notes: `Themed with brand colors · ${b.services?.length || 0} session options`,
  }));

  const brandEmbeds = brands.map((b) => ({
    brand: b,
    embedUrl: `${effectiveOrigin}/embed/schedule?brand=${b.slug}`,
    iframeCode: `<iframe src="${effectiveOrigin}/embed/schedule?brand=${b.slug}" style="width:100%;min-height:1100px;border:0;" title="Book with ${b.name}"></iframe>`,
  }));

  return (
    <AdminLayout title="Link Manager">
      <div className="max-w-5xl mx-auto" data-testid="admin-links-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]">Link Manager</h1>
          <p className="text-sm text-[#475569] mt-1">Every page and embed code in one place. Click to open, or copy to share.</p>
        </div>

        {/* PRODUCTION URL SETTING */}
        <section className="mb-6 bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="production-url-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Settings size={18} className="text-emerald-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-[#0A0F1C] text-sm">Production URL</h2>
              <p className="text-xs text-[#475569]">All URLs &amp; iframes below use this domain. Set to <code className="bg-[#F1F5F9] px-1 rounded text-[10px]">https://dcaenterprisesinc.com</code> when you deploy.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              value={productionUrl}
              onChange={(e) => setProductionUrl(e.target.value)}
              placeholder={`Currently using preview origin: ${ORIGIN}`}
              className="flex-1"
              data-testid="production-url-input"
            />
            <button
              onClick={saveProductionUrl}
              disabled={savingUrl}
              className="btn-primary-blue inline-flex items-center gap-1 px-4 py-2 text-sm whitespace-nowrap"
              data-testid="save-production-url-btn"
            >
              <Save size={14} /> {savingUrl ? 'Saving...' : 'Save'}
            </button>
          </div>
          {productionUrl && (
            <div className="mt-2 text-xs text-emerald-700 font-medium" data-testid="production-url-active">
              ✓ All links and iframes below use <code className="bg-emerald-50 px-1 rounded">{productionUrl}</code>
            </div>
          )}
        </section>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pages, embed codes, descriptions..."
            className="pl-9 bg-white"
            data-testid="links-search-input"
          />
        </div>

        {/* IFRAME EMBED SNIPPETS - prominent at the top */}
        <section className="mb-8" data-testid="iframe-snippets-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-[#F97316]/10 flex items-center justify-center">
              <Code2 size={18} className="text-[#F97316]" />
            </div>
            <div>
              <h2 className="font-bold text-[#0A0F1C]">Iframe Embed Snippets</h2>
              <p className="text-xs text-[#475569]">Paste these into WordPress Custom HTML blocks or Emergent HTML widgets on your other brand sites.</p>
            </div>
          </div>
          <div className="space-y-3">
            {brandEmbeds.length === 0 && (
              <div className="bg-white border border-dashed border-[#E2E8F0] rounded-xl p-6 text-center text-sm text-[#475569]">
                No active booking brands yet. <a href="/admin/booking-brands" className="text-[#1D4ED8] font-semibold hover:underline">Create one →</a>
              </div>
            )}
            {brandEmbeds.map(({ brand, embedUrl, iframeCode }) => (
              <div key={brand.slug} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`iframe-card-${brand.slug}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#F1F5F9]" style={{ background: brand.bg_color + '08' }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: brand.bg_color, color: brand.accent_color }}>
                      {brand.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#0A0F1C] truncate">{brand.name}</div>
                      <div className="text-[11px] text-[#475569]">/{brand.slug}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={embedUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC]" data-testid={`preview-iframe-${brand.slug}`}>
                      <ExternalLink size={11} /> Preview
                    </a>
                    <button
                      onClick={() => handleCopy(iframeCode, `iframe-${brand.slug}`)}
                      className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white"
                      style={{ background: brand.primary_color }}
                      data-testid={`copy-iframe-${brand.slug}`}
                    >
                      {copied === `iframe-${brand.slug}` ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy iframe</>}
                    </button>
                  </div>
                </div>
                <pre className="bg-[#0F172A] text-[#E2E8F0] p-3 text-[11px] overflow-x-auto leading-relaxed">{iframeCode}</pre>
              </div>
            ))}
          </div>
        </section>

        {/* PUBLIC PAGES */}
        {[...SECTIONS, { ...SECTIONS[0], id: 'brand-pages', icon: Users, title: 'Per-Brand Coaching Pages (On DCA)', description: 'On-DCA-site themed booking pages for each coaching brand', items: brandPagesItems }, ADMIN_SECTION].filter((s) => s.id !== 'main' || true).map((section) => {
          const items = filter(section.items);
          if (items.length === 0 && search.trim()) return null;
          const Icon = section.icon;
          return (
            <section key={section.id} className="mb-7" data-testid={`section-${section.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-[#1D4ED8]/10 flex items-center justify-center">
                  <Icon size={18} className="text-[#1D4ED8]" />
                </div>
                <div>
                  <h2 className="font-bold text-[#0A0F1C]">{section.title}</h2>
                  <p className="text-xs text-[#475569]">{section.description}</p>
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                {items.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-[#94A3B8] text-center">No links match your search.</div>
                ) : items.map((it, i) => {
                  const fullUrl = `${effectiveOrigin}${it.path}`;
                  const copyId = `${section.id}-${it.path}`;
                  const stats = analytics[it.path];
                  const conv = conversions[it.path];
                  const convRate = stats && stats.total_views > 0 && typeof conv === 'number' ? Math.round((conv / stats.total_views) * 100) : null;
                  return (
                    <div key={it.path} className={`flex items-center justify-between px-4 py-3 gap-4 ${i !== items.length - 1 ? 'border-b border-[#F1F5F9]' : ''}`} data-testid={`link-row-${it.path.replace(/[^a-z0-9]/gi, '-')}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-sm text-[#0A0F1C] truncate">{it.name}</span>
                          {it.notes && <span className="text-[10px] text-[#94A3B8] font-medium">· {it.notes}</span>}
                        </div>
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-mono text-[#1D4ED8] hover:underline inline-flex items-center gap-1 break-all"
                          data-testid={`link-url-${it.path.replace(/[^a-z0-9]/gi, '-')}`}
                        >
                          <LinkIcon size={10} />
                          {fullUrl}
                          <ExternalLink size={10} />
                        </a>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {stats && (
                          <div className="hidden sm:flex flex-col items-end text-[10px] leading-tight" data-testid={`stats-${it.path.replace(/[^a-z0-9]/gi, '-')}`}>
                            <div className="text-[#475569]"><span className="font-bold text-[#0A0F1C] text-xs">{stats.total_views.toLocaleString()}</span> views</div>
                            <div className="text-[#94A3B8]">{stats.seven_day_views} in 7d</div>
                            {convRate !== null && (
                              <div className="text-emerald-600 font-bold" data-testid={`conv-${it.path.replace(/[^a-z0-9]/gi, '-')}`}>{conv} → {convRate}%</div>
                            )}
                          </div>
                        )}
                        {!stats && typeof conv === 'number' && conv > 0 && (
                          <div className="hidden sm:flex flex-col items-end text-[10px] leading-tight">
                            <div className="text-emerald-600 font-bold">{conv} bookings</div>
                          </div>
                        )}
                        <button
                          onClick={() => handleCopy(fullUrl, copyId)}
                          className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC]"
                          data-testid={`copy-link-${it.path.replace(/[^a-z0-9]/gi, '-')}`}
                        >
                          {copied === copyId ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </AdminLayout>
  );
}
